/**
 * PartnerServiceAreas - the list of places this partner is allowed to be
 * offered deliveries in, and the form that adds another one.
 *
 * WHAT THESE ACTUALLY DO TODAY, HONESTLY
 * The server can match a drop off to a partner by service area:
 * `getAvailablePartnersByLocation` walks each partner's areas, takes the first
 * coordinate as the centre, and keeps the partner when the drop off is inside
 * radiusKm. As of Sep 14 2026 that matcher is now wired into the vendor's
 * partner picker, so an area you add here changes which orders you are offered.
 *
 * Two fields on this form are still recorded and nothing reads them:
 * `pricingTier` and the whole `slaCommitment` block. No fee is calculated from
 * a tier, no late delivery is measured against guaranteedMinutes, and no
 * penalty or bonus is ever charged or paid. They are captured so the agreement
 * is written down in one place; they are not enforcement. The form says so
 * rather than implying a number has teeth it does not have.
 */
import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { getUserFromLocalStorage } from "../../utilities/user";
import { apiFetchPartner, apiAddPartnerServiceArea } from "../../api/index.js";
import LocationPicker from "../../components/LocationPicker";
import {
  colors,
  font,
  text,
  card,
  cardBody,
  grid,
  inp,
  button,
  pill,
  ugx,
} from "./partnerTheme";
import PartnerHelp from "./partnerHelp";

/* Every 3PL endpoint answers failures in the same envelope. The server
   sentence is worth surfacing because its refusals name the field, and a
   generic "something went wrong" sends the partner to support instead of back
   to the box they typed in. */
const serverMessage = (err, fallback) =>
  err?.response?.data?.error?.message || fallback;

/* The schema defaults, restated here only so the form can tell the operator
   what the server will fall back to when a box is left blank. Keep in step
   with serviceAreaSchema in models/ThreePLPartnerModel.js. */
const DEFAULTS = {
  radiusKm: 10,
  pricingTier: 5000,
  guaranteedMinutes: 45,
  penalty: 2000,
  bonus: 1000,
};

const EMPTY_FORM = {
  name: "",
  lat: "",
  lng: "",
  radiusKm: "",
  pricingTier: "",
  guaranteedMinutes: "",
  penalty: "",
  bonus: "",
};

/* A blank box means "let the server apply its default", which is not the same
   as zero. Number("") is 0, so the blank has to be caught before the cast. */
const numberOrDefault = (raw, fallback) =>
  String(raw).trim() === "" ? fallback : Number(raw);

const Field = ({ label, help, children }) => (
  <div style={{ marginBottom: 18 }}>
    <span style={text.label}>{label}</span>
    {children}
    {help ? <p style={text.help}>{help}</p> : null}
  </div>
);

const PartnerServiceAreas = () => {
  const { agent } = useSelector((state) => state.auth);
  const user = agent || getUserFromLocalStorage();
  const partnerId = user?.result?.partnerId || user?.result?._id;
  const operationMode = user?.result?.operationMode;
  const isPartnerAccount = operationMode === "3PL_PARTNER";
  const permissions = user?.result?.permissions || {};

  /* The server re-checks access on the write, so this gate is not the security
     boundary. It only keeps the screen from offering a form whose one possible
     outcome would be a refusal. A service area sets the price the partner is
     paid per delivery, so it rides on financial access rather than fleet
     access. */
  // Must match the server: POST /:id/service-areas is checkStaffPermission("profile", "full").
  const canAddArea = isPartnerAccount || permissions?.profile === "full";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [areas, setAreas] = useState([]);
  const [companyName, setCompanyName] = useState("");

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const setField = (key) => (e) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  /* The centre point is still held as the two strings every other box holds,
     so readCoordinates and the payload below are untouched. The map just
     writes both of them at once, and only ever writes a whole pair. */
  const point =
    String(form.lat).trim() !== "" && String(form.lng).trim() !== ""
      ? { lat: Number(form.lat), lng: Number(form.lng) }
      : null;

  const setPoint = (next) =>
    setForm((prev) => ({
      ...prev,
      lat: next && Number.isFinite(next.lat) ? String(next.lat) : "",
      lng: next && Number.isFinite(next.lng) ? String(next.lng) : "",
    }));

  /* A blank radius box means the server applies its default, so the circle
     previews that same default rather than vanishing while the box is empty. */
  const previewRadiusKm = numberOrDefault(form.radiusKm, DEFAULTS.radiusKm);

  const load = useCallback(async () => {
    if (!partnerId) {
      setLoading(false);
      setError(
        "No partner account is attached to this login, so there are no service areas to show."
      );
      return;
    }
    try {
      setLoading(true);
      setError("");
      const res = await apiFetchPartner(partnerId);
      if (res?.data?.success) {
        const partner = res.data.data?.partner || {};
        setAreas(Array.isArray(partner.serviceAreas) ? partner.serviceAreas : []);
        setCompanyName(partner.companyName || "");
      } else {
        setError("The service areas could not be loaded.");
      }
    } catch (err) {
      setError(serverMessage(err, "The service areas could not be loaded."));
    } finally {
      setLoading(false);
    }
  }, [partnerId]);

  useEffect(() => {
    load();
  }, [load]);

  /* Latitude and longitude are the only two inputs that fail silently rather
     than loudly: a swapped pair or a stray digit still saves, and the only
     symptom is a partner who is never matched. So they are checked here, in
     words, before anything is sent. */
  const readCoordinates = () => {
    const lat = Number(form.lat);
    const lng = Number(form.lng);

    if (String(form.lat).trim() === "" || String(form.lng).trim() === "") {
      return { ok: false, message: "Enter both the latitude and the longitude of the centre point." };
    }
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return {
        ok: false,
        message: "Latitude and longitude have to be plain numbers, for example 0.3476 and 32.5825.",
      };
    }
    if (lat < -90 || lat > 90) {
      return { ok: false, message: "Latitude has to be between -90 and 90. Check you have not typed the longitude here." };
    }
    if (lng < -180 || lng > 180) {
      return { ok: false, message: "Longitude has to be between -180 and 180. Check you have not typed the latitude here." };
    }
    return { ok: true, lat, lng };
  };

  const submit = async (e) => {
    e.preventDefault();

    if (!form.name.trim()) {
      toast.error("Give the area a name, for example Nakawa or Kampala Central.");
      return;
    }

    const coords = readCoordinates();
    if (!coords.ok) {
      toast.error(coords.message);
      return;
    }

    const radiusKm = numberOrDefault(form.radiusKm, DEFAULTS.radiusKm);
    const pricingTier = numberOrDefault(form.pricingTier, DEFAULTS.pricingTier);
    const guaranteedMinutes = numberOrDefault(form.guaranteedMinutes, DEFAULTS.guaranteedMinutes);
    const penalty = numberOrDefault(form.penalty, DEFAULTS.penalty);
    const bonus = numberOrDefault(form.bonus, DEFAULTS.bonus);

    if (!Number.isFinite(radiusKm) || radiusKm <= 0) {
      toast.error("The radius has to be a number of kilometres above zero.");
      return;
    }
    if (![pricingTier, guaranteedMinutes, penalty, bonus].every((n) => Number.isFinite(n) && n >= 0)) {
      toast.error("The price and the three SLA figures have to be numbers, and none of them can be negative.");
      return;
    }

    try {
      setSaving(true);
      /* coordinates is an array of points on the schema, so that an area can
         later be drawn as a polygon. Today the matcher only ever reads the
         first point and treats it as the centre of a radiusKm circle, so one
         lat/lng pair is all the form collects and it is sent as a single
         element array to stay the shape the schema expects. */
      const res = await apiAddPartnerServiceArea(partnerId, {
        name: form.name.trim(),
        coordinates: [{ lat: coords.lat, lng: coords.lng }],
        radiusKm,
        pricingTier,
        slaCommitment: { guaranteedMinutes, penalty, bonus },
      });

      if (res?.data?.success) {
        const created = res.data.data?.serviceArea;
        if (created) {
          setAreas((prev) => [...prev, created]);
        } else {
          load();
        }
        setForm(EMPTY_FORM);
        setShowForm(false);
        toast.success("Service area added. You can now be matched to deliveries inside it.");
      } else {
        toast.error("The service area was not saved.");
      }
    } catch (err) {
      toast.error(serverMessage(err, "The service area was not saved."));
    } finally {
      setSaving(false);
    }
  };

  const th = {
    ...text.label,
    display: "table-cell",
    marginBottom: 0,
    padding: "10px 14px",
    textAlign: "left",
    borderBottom: `1px solid ${colors.line}`,
    whiteSpace: "nowrap",
  };
  const td = {
    ...text.body,
    padding: "12px 14px",
    borderBottom: `1px solid ${colors.lineSoft}`,
    verticalAlign: "top",
  };
  const numCell = { ...td, ...text.numeric, padding: "12px 14px" };

  if (loading) {
    return (
      <div className="content">
        <div style={{ ...card, ...cardBody, textAlign: "center" }}>
          <p style={{ ...text.body, margin: 0, color: colors.muted }}>Loading your service areas...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="content">
        <div style={{ ...card, ...cardBody, borderColor: colors.red }}>
          <h4 style={{ ...text.title, color: colors.red, marginBottom: 6 }}>
            Service areas could not load
          </h4>
          <p style={{ ...text.body, margin: "0 0 16px" }}>{error}</p>
          <button type="button" style={button("secondary")} onClick={load}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="content">
      {/* Header */}
      <div
        style={{
          ...card,
          ...cardBody,
          display: "flex",
          flexWrap: "wrap",
          gap: 12,
          alignItems: "flex-start",
          justifyContent: "space-between",
        }}
      >
        <div style={{ minWidth: 240, flex: "1 1 320px" }}>
          <h4 style={text.title}>
            Service Areas
            <PartnerHelp topic="serviceArea" />
          </h4>
          <p style={text.subtitle}>
            {companyName ? `${companyName} is offered deliveries only inside these areas. ` : ""}
            A drop off is matched to you when it falls within the radius of one of them.
          </p>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button type="button" style={button("secondary")} onClick={load}>
            Refresh
          </button>
          {canAddArea && areas.length > 0 && (
            <button
              type="button"
              style={button(showForm ? "quiet" : "primary")}
              onClick={() => setShowForm((v) => !v)}
            >
              {showForm ? "Cancel" : "Add area"}
            </button>
          )}
        </div>
      </div>

      {/* The list, or the reason there is nothing in it */}
      <div style={{ ...card, ...cardBody }}>
        <p style={text.section}>
          {areas.length === 1 ? "1 service area" : `${areas.length} service areas`}
        </p>

        {areas.length === 0 ? (
          <div style={{ padding: "28px 0", textAlign: "center" }}>
            <span style={{ ...pill("warn"), marginBottom: 12 }}>Not matched to any deliveries</span>
            <p style={{ ...text.body, margin: "0 0 6px", fontWeight: 600, color: colors.ink }}>
              You have no service areas yet.
            </p>
            <p style={{ ...text.body, margin: "0 auto 16px", color: colors.muted, maxWidth: 520 }}>
              Deliveries are matched by area. Until at least one area is here with a centre point and
              a radius, the vendor picker lists you below the partners who do cover it.
            </p>
            {canAddArea ? (
              <button type="button" style={button("primary")} onClick={() => setShowForm(true)}>
                Add your first area
              </button>
            ) : (
              <p style={{ ...text.help, maxWidth: 420, margin: "0 auto" }}>
                Ask the partner owner, or staff with full financial access, to add one.
              </p>
            )}
          </div>
        ) : (
          /* Its own scroller: five columns of figures would otherwise drag the
             whole page sideways on a laptop window. */
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", minWidth: 820, borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={th}>Area</th>
                  <th style={th}>Centre point</th>
                  <th style={th}>
                    Radius
                    <PartnerHelp topic="serviceAreaRadius" />
                  </th>
                  <th style={th}>
                    Price per delivery
                    <PartnerHelp topic="pricingTierRecorded" />
                  </th>
                  <th style={th}>
                    SLA commitment
                    <PartnerHelp topic="slaMoneyRecorded" />
                  </th>
                </tr>
              </thead>
              <tbody>
                {areas.map((area, index) => {
                  const points = Array.isArray(area.coordinates) ? area.coordinates : [];
                  const centre = points[0];
                  const sla = area.slaCommitment || {};
                  return (
                    <tr key={area._id || `${area.name}-${index}`}>
                      <td style={td}>
                        <div style={{ fontWeight: 600, color: colors.ink }}>
                          {area.name || "Unnamed area"}
                        </div>
                      </td>
                      <td style={td}>
                        {centre ? (
                          <>
                            <div style={{ fontFamily: font.mono, fontSize: 12, color: colors.body }}>
                              {centre.lat}, {centre.lng}
                            </div>
                            {points.length > 1 && (
                              <div style={{ ...text.help, margin: "4px 0 0" }}>
                                {points.length} points saved, the first one is used as the centre.
                              </div>
                            )}
                          </>
                        ) : (
                          <span style={pill("bad")}>No centre point</span>
                        )}
                      </td>
                      <td style={numCell}>{Number(area.radiusKm || 0)} km</td>
                      <td style={numCell}>{ugx(area.pricingTier)}</td>
                      <td style={td}>
                        <div style={{ ...text.numeric }}>
                          {Number(sla.guaranteedMinutes || 0)} min guaranteed
                        </div>
                        <div style={{ ...text.help, margin: "4px 0 0" }}>
                          Penalty {ugx(sla.penalty)}, bonus {ugx(sla.bonus)}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add form */}
      {canAddArea && showForm && (
        <form style={{ ...card, ...cardBody }} onSubmit={submit}>
          <p style={text.section}>Add a service area</p>

          <div style={{ ...grid(2, 20), gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
            <Field label="Area name" help="What your team calls it, for example Nakawa or Entebbe Road.">
              <input
                type="text"
                value={form.name}
                onChange={setField("name")}
                placeholder="Nakawa"
                {...inp()}
              />
            </Field>

            <Field
              label={
                <>
                  Radius in km
                  <PartnerHelp topic="serviceAreaRadius" />
                </>
              }
              help={`Leave blank for the default of ${DEFAULTS.radiusKm} km.`}
            >
              <input
                type="number"
                min="0"
                step="0.5"
                value={form.radiusKm}
                onChange={setField("radiusKm")}
                placeholder={String(DEFAULTS.radiusKm)}
                {...inp()}
              />
            </Field>

            {/* Full width: a map squeezed into one column of a two column
                grid is too small to aim with, which defeats the point of it. */}
            <div style={{ gridColumn: "1 / -1", marginBottom: 18 }}>
              <LocationPicker
                label="Centre point"
                help="Search for the place, click the map, or drag the pin. The shaded circle is what the radius above covers. Kampala sits near 0.3476, 32.5825."
                value={point}
                onChange={setPoint}
                radiusKm={previewRadiusKm}
              />
            </div>

            <Field
              label={
                <>
                  Price per delivery (UGX)
                  <PartnerHelp topic="pricingTierRecorded" />
                </>
              }
              help={`Leave blank for the default of ${ugx(DEFAULTS.pricingTier)}.`}
            >
              <input
                type="number"
                min="0"
                step="500"
                value={form.pricingTier}
                onChange={setField("pricingTier")}
                placeholder={String(DEFAULTS.pricingTier)}
                {...inp()}
              />
            </Field>

            <Field
              label={
                <>
                  Guaranteed minutes
                  <PartnerHelp topic="guaranteedMinutes" />
                </>
              }
              help={`Leave blank for the default of ${DEFAULTS.guaranteedMinutes} minutes.`}
            >
              <input
                type="number"
                min="0"
                step="5"
                value={form.guaranteedMinutes}
                onChange={setField("guaranteedMinutes")}
                placeholder={String(DEFAULTS.guaranteedMinutes)}
                {...inp()}
              />
            </Field>

            <Field
              label={
                <>
                  Late penalty (UGX)
                  <PartnerHelp topic="slaMoneyRecorded" />
                </>
              }
              help={`Charged when the guarantee is missed. Default ${ugx(DEFAULTS.penalty)}.`}
            >
              <input
                type="number"
                min="0"
                step="500"
                value={form.penalty}
                onChange={setField("penalty")}
                placeholder={String(DEFAULTS.penalty)}
                {...inp()}
              />
            </Field>

            <Field
              label={
                <>
                  On time bonus (UGX)
                  <PartnerHelp topic="slaMoneyRecorded" />
                </>
              }
              help={`Paid when the guarantee is met. Default ${ugx(DEFAULTS.bonus)}.`}
            >
              <input
                type="number"
                min="0"
                step="500"
                value={form.bonus}
                onChange={setField("bonus")}
                placeholder={String(DEFAULTS.bonus)}
                {...inp()}
              />
            </Field>
          </div>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 6 }}>
            <button
              type="submit"
              style={button("primary", saving ? { opacity: 0.6, cursor: "wait" } : {})}
              disabled={saving}
            >
              {saving ? "Saving" : "Save area"}
            </button>
            <button
              type="button"
              style={button("quiet")}
              disabled={saving}
              onClick={() => {
                setForm(EMPTY_FORM);
                setShowForm(false);
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default PartnerServiceAreas;
